import { PaginationData } from '../components/utils/Pagination';
import { Article, ArticleContent } from '../models/Article';

// Mock configuration - set to 0 for production builds, 1 for testing
// This can be temporarily changed to 1 for testing pagination
export const ENABLE_MOCK_ARTICLES = false; // Change to true to enable mock articles for testing

// Configuration for mock data
export const MOCK_CONFIG = {
  TOTAL_ARTICLES: 1000, // Total number of mock articles to simulate
  ARTICLES_PER_PAGE: 20, // Articles per page - set to 1 to test pagination with 47 pages
};

// Validate that mocks are disabled in production
if (process.env.NODE_ENV === 'production' && ENABLE_MOCK_ARTICLES) {
  throw new Error('ENABLE_MOCK_ARTICLES must be false for production builds');
}

/**
 * Generates mock article data
 */
function generateMockArticle(id: number): Article {
  const statuses: Array<Article['status']> = ['PENDING_PREVIEW', 'DRAFT', 'FAILED'];
  const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];

  return {
    id: `mock-article-${id}`,
    title: `Mock Article ${id}: ${getMockTitle(id)}`,
    status: randomStatus,
    createdAt: new Date(Date.now() - id * 24 * 60 * 60 * 1000).toISOString(), // Stagger dates
    updatedAt: new Date(Date.now() - id * 12 * 60 * 60 * 1000).toISOString(),
    resultLocation: `mock-content-${id}.json`,
    articleLocation: `mock-article-${id}.pdf`,
    ownerId: 1,
    ownerExternalId: `mock-owner-${id}`,
  };
}

/**
 * Generates mock article content
 */
function generateMockArticleContent(articleId: string): ArticleContent {
  const id = parseInt(articleId.replace('mock-article-', ''));

  return {
    imageCarousel: {
      type: 'ImageCarousel',
      id: `mock-carousel-${id}`,
      properties: {
        id: `mock-carousel-props-${id}`,
        heading: {
          id: `mock-heading-${id}`,
          content: `Mock Article ${id}: ${getMockTitle(id)}`,
        },
        items: [
          {
            imageUrl: {
              id: `mock-image-${id}`,
              url: `https://picsum.photos/400/300?random=${id}`,
            },
            heading: {
              id: `mock-item-heading-${id}`,
              content: `Mock Article ${id}: ${getMockTitle(id)}`,
            },
            text: {
              id: `mock-item-text-${id}`,
              content: `This is a mock article ${id} that demonstrates pagination functionality. It contains sample content for testing purposes.`,
            },
            linkable: true,
            linkTo: `articles/mock-article-${id}`,
          },
        ],
      },
    },
    headline: {
      type: 'StandardHeaderText',
      id: `mock-headline-${id}`,
      properties: {
        id: `mock-headline-props-${id}`,
        heading: {
          id: `mock-headline-heading-${id}`,
          content: `Mock Article ${id}: ${getMockTitle(id)}`,
        },
        content: {
          id: `mock-headline-content-${id}`,
          content: `This is a mock article ${id} that demonstrates pagination functionality.`,
        },
      },
    },
    extendedSummary: [],
  };
}

/**
 * Gets a mock title based on the article ID
 */
function getMockTitle(id: number): string {
  const titles = [
    'Renewable Energy Investment Opportunities',
    'Market Analysis for Q4 2024',
    'Sustainable Development Goals Progress',
    'Technology Sector Outlook',
    'ESG Investment Strategies',
    'Real Estate Market Trends',
    'Healthcare Innovation Funding',
    'Climate Change Investment Impact',
    'Emerging Markets Analysis',
    'Digital Transformation Investments',
    'Infrastructure Development Plans',
    'Green Bond Market Update',
    'Alternative Investment Strategies',
    'Risk Management Best Practices',
    'Portfolio Diversification Techniques',
    'Cryptocurrency Market Analysis',
    'Impact Investing Opportunities',
    'Global Economic Forecast',
    'Regulatory Changes Impact',
    'Investment Technology Trends',
  ];

  return titles[id % titles.length] || `Investment Opportunity ${id}`;
}

/**
 * Simulates the API response for pending preview articles with pagination
 */
export function getMockPendingPreviewArticles(
  page: number = 1,
  limit: number = MOCK_CONFIG.ARTICLES_PER_PAGE,
): Promise<{ data: Article[]; pagination: PaginationData }> {
  if (!ENABLE_MOCK_ARTICLES) {
    throw new Error('Mock articles are disabled');
  }

  return new Promise((resolve) => {
    // Simulate API delay
    setTimeout(() => {
      const totalArticles = MOCK_CONFIG.TOTAL_ARTICLES;
      const totalPages = Math.ceil(totalArticles / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = Math.min(startIndex + limit, totalArticles);

      const articles: Article[] = [];
      for (let i = startIndex; i < endIndex; i++) {
        articles.push(generateMockArticle(i + 1));
      }

      const pagination: PaginationData = {
        page,
        limit,
        total: totalArticles,
        totalPages,
      };

      resolve({ data: articles, pagination });
    }, 300); // 300ms delay to simulate network
  });
}

/**
 * Simulates getting article content for a mock article
 */
export function getMockArticleContent(article: Article): Promise<ArticleContent> {
  if (!ENABLE_MOCK_ARTICLES) {
    throw new Error('Mock articles are disabled');
  }

  return new Promise((resolve) => {
    // Simulate API delay
    setTimeout(() => {
      resolve(generateMockArticleContent(article.id));
    }, 100); // Shorter delay for content
  });
}

/**
 * Development helper to change mock configuration
 */
export function setMockConfig(config: Partial<typeof MOCK_CONFIG>): void {
  if (!ENABLE_MOCK_ARTICLES) {
    console.warn('Mock articles are disabled, cannot change configuration');
    return;
  }

  Object.assign(MOCK_CONFIG, config);
  console.log('Mock configuration updated:', MOCK_CONFIG);
}

// Development helpers - only available in development
if (ENABLE_MOCK_ARTICLES && typeof window !== 'undefined') {
  // Add to window for easy access in dev tools
  (window as any).mockArticles = {
    setMockConfig,
    MOCK_CONFIG,
    generateMockArticle,
    generateMockArticleContent,
  };

  console.log('Mock articles enabled. Use window.mockArticles to configure in dev tools.');
  console.log('Current config:', MOCK_CONFIG);
}
